import { ChevronLeft, Eye, ImagePlus, Trash2, X } from "lucide-react";
import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Button } from "../../../ui/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../../ui/components/ui/dialog";
import { Input } from "../../../ui/components/ui/input";
import { FeatureHintInhalt } from "../../feature-hints/FeatureHintAnsicht";
import { ANBIETER_NAME, VideoKarte } from "../../feature-hints/VideoKarte";
import { KundenVorschau } from "./KundenVorschau";
import { leereUmfrage, UmfrageFelder } from "./UmfrageFelder";
import { GROUP_LABELS } from "../../feature-hints/NeuigkeitenBereich";
import { alsDeutschesDatum, alsZeitpunkt } from "../../feature-hints/datum";
import {
  Neuigkeit,
  NeuigkeitAktion,
  NeuigkeitZahlen,
  NeuigkeitEingabe,
  NeuigkeitEinordnung,
  NeuigkeitUmfrage,
  NeuigkeitVideo,
  STAND_LABEL,
  STAND_PLAKETTE,
  useAdminNeuigkeiten,
} from "./useAdminNeuigkeiten";

/**
 * Abgeleitet aus dem, was der Kunde liest - nicht noch einmal getippt. Diese Maske soll
 * zeigen, was beim Kunden ankommt; zwei Listen fuer dieselben fuenf Woerter wuerden die
 * Vorschau beim ersten Aendern still unwahr machen (KAN-290).
 */
const EINORDNUNGEN: { wert: NeuigkeitEinordnung; label: string }[] = (
  Object.keys(GROUP_LABELS) as NeuigkeitEinordnung[]
).map(wert => ({ wert, label: GROUP_LABELS[wert] }));

/** Dieselben Grenzen, die der Server zieht — hier nur, um frueher Bescheid zu sagen. */
const ERLAUBTE_BILDTYPEN = ["image/jpeg", "image/png", "image/webp"];
const MAX_BILD_GROESSE = 5000000;

/**
 * Dieselbe Anbieterliste, die der Server zieht — hier nur, um frueher Bescheid zu sagen
 * (KAN-292). Entschieden wird weiter auf dem Server; diese Pruefung sorgt nur dafuer, dass
 * ein Tippfehler unter dem Feld steht statt in einem Fenster ueber der ganzen Seite.
 */
const VIDEO_HOSTS = ["youtube.com", "youtu.be", "vimeo.com", "loom.com"];

const linkBeanstandung = (url: string): string | null => {
  const getrimmt = url.trim();
  if (!getrimmt) return null;

  let adresse: URL;
  try {
    adresse = new URL(getrimmt);
  } catch {
    return "Das sieht nicht nach einer vollständigen Adresse aus.";
  }

  if (adresse.protocol !== "https:") return "Der Link muss mit https:// beginnen.";

  const host = adresse.hostname.toLowerCase().replace(/^www\.|^m\.|^music\.|^player\./, "");
  if (!VIDEO_HOSTS.includes(host)) return "Nur YouTube, Vimeo oder Loom. Dieser Anbieter geht nicht.";

  return null;
};

/**
 * Wie sich ein Schreibfeld der Karte beim Hineinklicken zeigt.
 *
 * Der blaue Kasten des Browsers passt nicht zu einer Karte, die aussehen soll wie beim
 * Kunden. Ganz wegnehmen waere aber schlechter: Wer mit der Tastatur arbeitet, saehe dann
 * nicht mehr, wo er steht. Stattdessen ein ruhiger Hintergrund, der mit dem Feld mitgeht.
 */
/**
 * Chrome faerbt ein Feld hellblau, sobald es den Wert fuer automatisch ausgefuellt haelt -
 * auch bei einem eingefuegten Link. Die eigene Hintergrundfarbe setzt sich dagegen nicht
 * durch; nur ein Innenschatten ueberdeckt sie. Die Schriftfarbe muss mit, sonst bleibt sie
 * Chromes eigene.
 */
const KEIN_AUTOFILL_BLAU =
  "autofill:shadow-[inset_0_0_0_1000px_white] autofill:[-webkit-text-fill-color:theme(colors.gray.900)]";

const FOKUS_FELD =
  "rounded-md outline-none transition-colors focus:bg-gray-50 focus:shadow-none focus-visible:outline-none";

/** Sagt, was an der Datei nicht passt — oder nichts, wenn sie durchgeht. */
const bildBeanstandung = (datei: File): string | null => {
  if (!ERLAUBTE_BILDTYPEN.includes(datei.type)) {
    return `„${datei.name}" ist kein JPG, PNG oder WebP${datei.type ? ` (${datei.type})` : ""}.`;
  }
  if (datei.size > MAX_BILD_GROESSE) {
    const mb = (datei.size / 1000000).toFixed(1);
    return `„${datei.name}" ist mit ${mb} MB zu groß. Erlaubt sind 5 MB.`;
  }
  return null;
};

const heute = (): string => new Date().toISOString().slice(0, 10);

/**
 * Die Fragen in den leeren Feldern haengen an der Einordnung: Bei einer Erinnerung gibt es
 * kein geloestes Problem, und bei etwas Wissenswertem haben wir nichts geaendert. Die
 * Platzhalter sind das einzige, was beim Schreiben anleitet — stehen dort die falschen
 * Fragen, entsteht der falsche Text.
 */
const PLATZHALTER: Record<NeuigkeitEinordnung, { problem: string; loesung: string }> = {
  neu: { problem: "Welches Problem kennt der Kunde aus seinem Alltag?", loesung: "Was haben wir daran geändert?" },
  verbessert: { problem: "Was war daran bisher umständlich?", loesung: "Was geht jetzt leichter?" },
  behoben: { problem: "Was ging bisher schief?", loesung: "Was haben wir repariert?" },
  erinnerung: { problem: "Woran soll der Kunde denken?", loesung: "Was ist dafür zu tun, und bis wann?" },
  wissenswert: { problem: "Was ist der Anlass?", loesung: "Was sollte der Kunde darüber wissen?" },
};

/**
 * Was dem Eintrag zum Veroeffentlichen fehlt, oder nichts.
 *
 * Dieselbe Regel, die der Server zieht (A2): Titel und mindestens einer der beiden Texte.
 * Hier steht sie, damit der Knopf gar nicht erst klickbar ist — ein 400 vom Server ist
 * keine Auskunft, sondern eine Zumutung.
 */
const fehltZumVeroeffentlichen = (entwurf: Entwurf): string | null => {
  const ohneTitel = !entwurf.title.trim();
  const ohneText = !entwurf.problemText.trim() && !entwurf.solutionText.trim();

  if (ohneTitel && ohneText) return "Titel und Text fehlen";
  if (ohneTitel) return "Titel fehlt";
  if (ohneText) return "Text fehlt";
  return null;
};

/** Anteil in Prozent, oder nichts, wenn es keinen Bezug gibt. */
const anteil = (teil: number, ganzes: number): string | undefined =>
  ganzes > 0 ? `${Math.round((teil / ganzes) * 100)} %` : undefined;

/**
 * Die drei Kennzahlen einer Neuigkeit (KAN-296).
 *
 * Alle drei beziehen sich auf dasselbe: auf die, die den Eintrag haetten sehen koennen.
 * Vorher mass die erste Kachel gegen alle Nutzer und die zweite gegen die Seher - zwei
 * Nenner in zwei gleich aussehenden Kacheln, und niemand konnte das raten.
 *
 * Eine Oeffnungsrate im Sinne von "geoeffnet geteilt durch gesehen" steht deshalb nicht
 * mehr da. Bei einer einzigen Person waeren 100 % ohnehin eine Scheingenauigkeit.
 */
const kennzahlen = (zahlen: NeuigkeitZahlen): { titel: string; wert: string; erklaerung: string }[] => {
  /** "1 von 26" - oder nur die Zahl, solange es keinen Bezug gibt. */
  const vonAus = (teil: number, ganzes: number): string => (ganzes > 0 ? `${teil} von ${ganzes}` : String(teil));

  /** "Personen (4 %)" - die Klammer faellt weg, wenn sich nichts ins Verhaeltnis setzen laesst. */
  const mitQuote = (wort: string, teil: number, ganzes: number): string => {
    const quote = anteil(teil, ganzes);
    return quote ? `${wort} (${quote})` : wort;
  };

  return [
    {
      titel: "Gesehen",
      wert: vonAus(zahlen.erreicht, zahlen.moeglich),
      erklaerung: mitQuote("Personen", zahlen.erreicht, zahlen.moeglich),
    },
    {
      titel: "Geöffnet",
      wert: vonAus(zahlen.geoeffnet, zahlen.moeglich),
      erklaerung: mitQuote("Personen", zahlen.geoeffnet, zahlen.moeglich),
    },
    {
      titel: zahlen.betriebeGesamt === 1 ? "Betrieb" : "Betriebe",
      wert: vonAus(zahlen.betriebe, zahlen.betriebeGesamt),
      erklaerung: mitQuote("erreicht", zahlen.betriebe, zahlen.betriebeGesamt),
    },
  ];
};

/**
 * Der Vorschlag im Zeitpunkt-Feld: morgen um acht, in der Schreibweise, die
 * `input[type=datetime-local]` erwartet (oertliche Zeit, ohne Zone).
 */
const morgenFruehLokal = (): string => {
  const zeitpunkt = new Date();
  zeitpunkt.setDate(zeitpunkt.getDate() + 1);
  zeitpunkt.setHours(8, 0, 0, 0);
  const zweistellig = (zahl: number): string => String(zahl).padStart(2, "0");
  return `${zeitpunkt.getFullYear()}-${zweistellig(zeitpunkt.getMonth() + 1)}-${zweistellig(zeitpunkt.getDate())}T${zweistellig(zeitpunkt.getHours())}:${zweistellig(zeitpunkt.getMinutes())}`;
};

interface Entwurf {
  title: string;
  problemText: string;
  solutionText: string;
  group: NeuigkeitEinordnung;
  date: string;
  opensDialog: boolean;
  actions: NeuigkeitAktion[];
  /** Der eingefuegte Link; leer heisst kein Video (A12). */
  /** Die Videos, in Reihenfolge. Beliebig viele (KAN-291). */
  videos: { url: string; title: string }[];
  /** Die Umfrage, wenn der Eintrag eine hat (KAN-302). */
  umfrage: NeuigkeitUmfrage | null;
}

/** Der Dialog ist beim neuen Eintrag an: Wer eine Neuigkeit schreibt, will gesehen werden. */
const leererEntwurf = (): Entwurf => ({
  title: "",
  problemText: "",
  solutionText: "",
  group: "neu",
  date: heute(),
  opensDialog: true,
  actions: [],
  videos: [],
  umfrage: null,
});

/**
 * Die Schreibflaeche fuer eine Neuigkeit (KAN-276, A1–A5, A7, A10).
 *
 * Gewaehlt wurde Variante C: Die Karte, die der Kunde spaeter sieht, ist zugleich das
 * Eingabefeld. Titel und Absaetze werden direkt darin getippt, die Einstellungen liegen als
 * schmale Leiste darunter. Beim Schreiben stehen die bisherigen Eintraege bewusst nicht
 * daneben — beim Anlegen braucht man sie nicht.
 *
 * Statt eines frei bearbeitbaren Bereichs sind es Eingabefelder ohne Rahmen: Sie sehen aus
 * wie der fertige Text, halten den Stand aber zuverlaessig — ein frei bearbeitbarer Bereich
 * und React-Zustand vertragen sich beim Tippen nicht.
 */
/**
 * Ein Textfeld, das mit seinem Inhalt waechst (KAN-299).
 *
 * Feste drei Zeilen reichten, solange hier zwei Saetze standen. Seit der Text als Markdown
 * gesetzt werden kann, schreibt jemand auch mal zwanzig Zeilen - und sah davon drei, mit
 * einem Rollbalken im Feld. Die Karte soll den ganzen Entwurf zeigen, so wie sie auch den
 * fertigen Eintrag zeigt.
 */
const MitwachsendesFeld = ({
  id,
  wert,
  onChange,
  platzhalter,
  className,
  mindestZeilen = 5,
}: {
  id: string;
  wert: string;
  onChange: (wert: string) => void;
  platzhalter: string;
  className: string;
  mindestZeilen?: number;
}) => {
  const feld = useRef<HTMLTextAreaElement>(null);

  // Erst auf "auto" zuruecksetzen, sonst waechst die Hoehe nur und schrumpft nie wieder.
  useEffect(() => {
    const element = feld.current;
    if (!element) return;
    element.style.height = "auto";
    element.style.height = `${element.scrollHeight}px`;
  }, [wert]);

  return (
    <textarea
      ref={feld}
      id={id}
      value={wert}
      onChange={event => onChange(event.target.value)}
      rows={mindestZeilen}
      placeholder={platzhalter}
      className={className}
    />
  );
};

export const NeuigkeitEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const istNeu = id === "neu";

  const { neuigkeiten, ziele, vorschauHolen, laedt, einzelnLaden, anlegen, aendern, veroeffentlichen, zurueckziehen, loeschen, bildHochladen, bildEntfernen } =
    useAdminNeuigkeiten();

  // Der eine Eintrag, direkt geholt (KAN-289). Er traegt die Maske, bis die Liste da ist;
  // danach fuehrt die Liste, weil sie nach jedem Speichern frisch kommt.
  const [direktGeholt, setDirektGeholt] = useState<Neuigkeit | null>(null);

  useEffect(() => {
    if (!id || id === "neu") return undefined;
    let abgebrochen = false;
    einzelnLaden(id).then(eintrag => {
      if (!abgebrochen) setDirektGeholt(eintrag);
    });
    return () => {
      abgebrochen = true;
    };
  }, [id, einzelnLaden]);

  const gespeichert = useMemo(() => {
    const basis = neuigkeiten.find(eintrag => eintrag.id === id) ?? (direktGeholt?.id === id ? direktGeholt : undefined);
    if (!basis) return undefined;

    // Der Inhalt kommt aus der Liste, weil die nach jedem Speichern frisch gelesen wird.
    // Die Aufschluesselung nach Knopf und Video traegt aber nur der Einzelabruf: Die
    // Uebersicht holt bewusst nur die Kernzahlen, sonst waeren es vier Abfragen je Zeile.
    const einzeln = direktGeholt?.id === id ? direktGeholt : undefined;
    return einzeln?.zahlen ? { ...basis, zahlen: einzeln.zahlen } : basis;
  }, [neuigkeiten, id, direktGeholt]);

  const [entwurf, setEntwurf] = useState<Entwurf>(leererEntwurf());
  const [geladen, setGeladen] = useState(false);
  const [geaendert, setGeaendert] = useState(false);
  const [meldung, setMeldung] = useState<{ art: "fehler" | "erfolg"; text: string } | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  const bildFeld = useRef<HTMLInputElement>(null);
  const [ueberZiehen, setUeberZiehen] = useState(false);
  const [zeitpunktOffen, setZeitpunktOffen] = useState(false);
  const [loeschenOffen, setLoeschenOffen] = useState(false);
  const [loeschfehler, setLoeschfehler] = useState(false);
  const [vorschauOffen, setVorschauOffen] = useState(false);
  // Sofort ist voreingestellt: Das ist der haeufige Fall (A11).
  const [sofort, setSofort] = useState(true);
  const [zeitpunkt, setZeitpunkt] = useState(morgenFruehLokal);

  // Den gespeicherten Stand genau einmal uebernehmen. Ohne das wuerde jeder frische Abruf
  // die gerade getippten Saetze ueberschreiben.
  useEffect(() => {
    if (istNeu || geladen || !gespeichert) return;
    setEntwurf({
      title: gespeichert.title,
      problemText: gespeichert.problemText,
      solutionText: gespeichert.solutionText,
      group: gespeichert.group,
      date: gespeichert.date,
      opensDialog: gespeichert.opensDialog,
      actions: gespeichert.actions ?? [],
      umfrage: gespeichert.umfrage ?? null,
      videos: gespeichert.videos.map(video => ({ url: video.url, title: video.title })),
    });
    setGeladen(true);
  }, [istNeu, geladen, gespeichert]);

  const setzen = <K extends keyof Entwurf>(feld: K, wert: Entwurf[K]) => {
    setEntwurf(vorher => ({ ...vorher, [feld]: wert }));
    setGeaendert(true);
    setMeldung(null);
  };

  const aktionSetzen = (index: number, teil: Partial<NeuigkeitAktion>) => {
    setEntwurf(vorher => ({
      ...vorher,
      actions: vorher.actions.map((aktion, nummer) => (nummer === index ? { ...aktion, ...teil } : aktion)),
    }));
    setGeaendert(true);
    setMeldung(null);
  };

  /**
   * Was wirklich zum Server geht - nicht der ganze Entwurf.
   *
   * Zwei Dinge bleiben hier: das angezeigte Datum, das der Server aus dem Erscheinen bildet
   * (KAN-281), und ein Knopf ohne Beschriftung oder Ziel. Der waere beim Server eine Absage
   * fuer den ganzen Eintrag, obwohl ein Entwurf sich jederzeit speichern lassen soll
   * (KAN-282). Er steht in der Maske weiter da und geht mit, sobald er ausgefuellt ist.
   */
  /**
   * Was wir zu einem eingefuegten Link wissen, nach Link abgelegt (KAN-292).
   *
   * Der Super-Admin soll sehen, was er eingefuegt hat, bevor er speichert. Gespeicherte
   * Videos bringen ihre Angaben schon mit; fuer alles andere fragt die Maske kurz nach.
   */
  const [vorschauen, setVorschauen] = useState<Record<string, NeuigkeitVideo | null>>({});

  useEffect(() => {
    const offen = entwurf.videos
      .map(video => video.url.trim())
      .filter(url => {
        if (!url || linkBeanstandung(url) || url in vorschauen) return false;
        const bekannt = gespeichert?.videos.find(video => video.url === url);
        // Bekannt und mit Anbieter-Titel: nichts zu holen. Bestand aus der Zeit vor KAN-293
        // traegt ihn nicht - dann fragen wir einmal nach, sonst gaebe es keinen Weg zurueck.
        return !bekannt || !bekannt.providerTitle;
      });
    if (offen.length === 0) return;

    // Kurz warten: Beim Tippen oder Einfuegen entstehen sonst Anfragen zu jedem Zwischenstand.
    const zeitgeber = setTimeout(() => {
      offen.forEach(async url => {
        const gefunden = await vorschauHolen(url);
        setVorschauen(vorher => ({ ...vorher, [url]: gefunden }));
      });
    }, 600);
    return () => clearTimeout(zeitgeber);
  }, [entwurf.videos, gespeichert, vorschauen, vorschauHolen]);

  /** Das fertige Video zu einem Link - aus dem Gespeicherten oder aus der Vorschau. */
  const videoZuLink = (url: string): NeuigkeitVideo | null =>
    gespeichert?.videos.find(video => video.url === url.trim()) ?? vorschauen[url.trim()] ?? null;

  /**
   * Wie der Anbieter das Video nennt. Am Gespeicherten steht es seit KAN-293; bei aelteren
   * Eintraegen kommt es aus der nachgeholten Vorschau.
   */
  const anbieterTitel = (url: string): string | null =>
    gespeichert?.videos.find(video => video.url === url.trim())?.providerTitle ?? vorschauen[url.trim()]?.title ?? null;

  /**
   * Der Entwurf als das, was der Kunde bekommt (KAN-299).
   *
   * Dieselben Felder, die auch gespeichert werden - nur die Knopf-Ziele sind hier schon
   * aufgeloest, weil die Kundenansicht fertige Adressen erwartet. Knoepfe ohne
   * Beschriftung bleiben draussen: Sie werden beim Speichern ohnehin verworfen und
   * stuenden in der Vorschau als leere Flaechen da.
   */
  const alsKundeninhalt = (): FeatureHintInhalt => ({
    title: entwurf.title,
    problemText: entwurf.problemText,
    solutionText: entwurf.solutionText,
    imageUrl: gespeichert?.imageUrl ?? null,
    videos: entwurf.videos
      .map(eintrag => {
        const video = videoZuLink(eintrag.url);
        return video ? { ...video, title: eintrag.title || video.title } : null;
      })
      .filter((video): video is NeuigkeitVideo => video !== null),
    umfrage: entwurf.umfrage
      ? {
          frage: entwurf.umfrage.frage,
          optionen: entwurf.umfrage.optionen.filter(option => option.trim()).map((label, stelle) => ({ stelle, label })),
          mehrfach: entwurf.umfrage.mehrfach,
          nichtAnonym: entwurf.umfrage.betriebeZeigen,
          meineAntwort: [],
        }
      : null,
    actions: entwurf.actions
      .filter(aktion => aktion.label.trim() && aktion.target.trim())
      .map(aktion => ({
        label: aktion.label,
        kind: aktion.kind,
        href: aktion.kind === "intern" ? (ziele.find(ziel => ziel.key === aktion.target)?.path ?? "/") : aktion.target,
      })),
  });

  const nutzlast = (): NeuigkeitEingabe => ({
    title: entwurf.title,
    problemText: entwurf.problemText,
    solutionText: entwurf.solutionText,
    group: entwurf.group,
    opensDialog: entwurf.opensDialog,
    actions: entwurf.actions.filter(aktion => aktion.label.trim() && aktion.target.trim()),
    // Leere Antwortzeilen fallen weg; wer eine anlegt und nicht ausfuellt, soll beim Kunden
    // keine leere Flaeche erzeugen.
    umfrage: entwurf.umfrage
      ? {
          frage: entwurf.umfrage.frage,
          optionen: entwurf.umfrage.optionen.filter(option => option.trim()).map(label => ({ label })),
          mehrfach: entwurf.umfrage.mehrfach,
          betriebeZeigen: entwurf.umfrage.betriebeZeigen,
        }
      : null,
    // Ein Eintrag ohne Link ist ein angefangener, noch nicht ausgefuellter - derselbe
    // Gedanke wie beim halbfertigen Knopf (KAN-282).
    videos: entwurf.videos.filter(video => video.url.trim()),
    // Von welchem Stand aus wir arbeiten. Hat inzwischen jemand anderes gespeichert, sagt
    // der Server ab, statt dessen Arbeit zu loeschen (KAN-286).
    ...(gespeichert?.updatedAt ? { basedOn: gespeichert.updatedAt } : {}),
  });

  const speichern = async (): Promise<string | null> => {
    setArbeitet(true);
    if (istNeu) {
      const angelegt = await anlegen(nutzlast());
      setArbeitet(false);
      if (!angelegt) {
        setMeldung({ art: "fehler", text: "Das Speichern hat nicht geklappt. Dein Text steht noch da." });
        return null;
      }
      setGeaendert(false);
      navigate(`/super-admin/neuigkeiten/${angelegt.id}`, { replace: true });
      return angelegt.id;
    }

    if (!id) return null;
    const { geklappt, konflikt } = await aendern(id, nutzlast());
    setArbeitet(false);
    if (!geklappt) {
      setMeldung({
        art: "fehler",
        text: konflikt
          ? "Jemand anderes hat diese Neuigkeit inzwischen geändert. Lade die Seite neu, bevor du speicherst. Dein Text steht so lange hier."
          : "Das Speichern hat nicht geklappt. Dein Text steht noch da.",
      });
      return null;
    }
    setGeaendert(false);
    setMeldung({ art: "erfolg", text: "Gespeichert." });
    return id;
  };

  const loeschenJetzt = async () => {
    if (!id) return;
    setArbeitet(true);
    const geklappt = await loeschen(id);
    setArbeitet(false);

    // Scheitert es, bleibt der Entwurf vollstaendig erhalten — dann sagt es das Fenster,
    // statt still zuzugehen und so zu tun, als waere er weg (A5).
    if (!geklappt) {
      setLoeschfehler(true);
      return;
    }
    navigate("/super-admin/neuigkeiten");
  };

  const zeitpunktFensterOeffnen = () => {
    setSofort(true);
    setZeitpunkt(morgenFruehLokal());
    setZeitpunktOffen(true);
  };

  const veroeffentlichenMitSpeichern = async (zeitpunkt?: string) => {
    const zielId = geaendert || istNeu ? await speichern() : id;
    if (!zielId) return;

    setArbeitet(true);
    const geklappt = await veroeffentlichen(zielId, zeitpunkt);
    setArbeitet(false);
    setZeitpunktOffen(false);

    if (!geklappt) {
      setMeldung({ art: "fehler", text: "Veröffentlichen hat nicht geklappt. Titel und Text müssen ausgefüllt sein." });
      return;
    }

    // Zurueck in die Liste: Dort steht der neue Stand als Plakette, und das ist die
    // Auskunft, die nach dem Veroeffentlichen zaehlt. Im Formular stehenzubleiben hiesse,
    // einen Eintrag vor sich zu haben, an dem es gerade nichts mehr zu tun gibt.
    navigate("/super-admin/neuigkeiten");
  };

  const zurueckziehenJetzt = async () => {
    if (!id) return;
    setArbeitet(true);
    const geklappt = await zurueckziehen(id);
    setArbeitet(false);
    setMeldung(
      geklappt
        ? { art: "erfolg", text: "Zurückgezogen. Der Eintrag ist wieder ein Entwurf und bei keinem Kunden mehr zu sehen." }
        : { art: "fehler", text: "Zurückziehen hat nicht geklappt. Der Eintrag ist weiter sichtbar." }
    );
  };

  const bildUebernehmen = async (datei: File) => {
    // Ohne gespeicherten Eintrag gibt es noch nichts, woran das Bild haengen koennte.
    const zielId = istNeu ? await speichern() : id;
    if (!zielId) return;

    setArbeitet(true);
    const geklappt = await bildHochladen(zielId, datei);
    setArbeitet(false);
    if (!geklappt) setMeldung({ art: "fehler", text: "Das Bild ließ sich nicht hochladen. Erlaubt sind JPG, PNG und WebP bis 5 MB." });
  };

  // Beide Wege pruefen gleich: Der Dateidialog laesst genauso Unpassendes durch wie das
  // Ziehen, und ein Satz an Ort und Stelle ist klarer als ein 400 vom Server.
  const bildPruefenUndUebernehmen = async (datei: File) => {
    const beanstandung = bildBeanstandung(datei);
    if (beanstandung) {
      setMeldung({ art: "fehler", text: beanstandung });
      return;
    }
    await bildUebernehmen(datei);
  };

  const bildGewaehlt = async (event: ChangeEvent<HTMLInputElement>) => {
    const datei = event.target.files?.[0];
    event.target.value = "";
    if (datei) await bildPruefenUndUebernehmen(datei);
  };

  const bildFallenGelassen = async (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setUeberZiehen(false);
    const datei = event.dataTransfer.files?.[0];
    if (datei) await bildPruefenUndUebernehmen(datei);
  };

  if (!istNeu && laedt && !geladen) return <p className="text-sm text-gray-500">Wird geladen …</p>;
  if (!istNeu && !laedt && !gespeichert) return <p className="text-sm text-gray-500">Diese Neuigkeit gibt es nicht mehr.</p>;

  const fehlendes = fehltZumVeroeffentlichen(entwurf);
  const stand = gespeichert?.status ?? "entwurf";
  const veroeffentlicht = stand === "veroeffentlicht";
  const geplant = stand === "geplant";

  return (
    <>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <Link to="/super-admin/neuigkeiten" className="inline-flex items-center gap-1.5 text-sm text-gray-600">
            <ChevronLeft className="h-4 w-4" />
            Alle Neuigkeiten
          </Link>
          <span className="h-5 w-px bg-gray-200" />
          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${STAND_PLAKETTE[stand]}`}>{STAND_LABEL[stand]}</span>
          {geplant && gespeichert?.publishAt && (
            <span className="text-[13px] text-gray-500">für {alsZeitpunkt(gespeichert.publishAt)}</span>
          )}
          {geaendert && <span className="text-[13px] text-gray-500">nicht gespeichert</span>}
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Seit der Text als Markdown gesetzt wird, zeigt die Maske nicht mehr, was der
              Kunde sieht - dieser Knopf holt es daneben (KAN-299). */}
          <Button
            size="kompakt"
            variant="outline"
            onClick={() => setVorschauOffen(offen => !offen)}
            className="gap-1.5 shadow-none"
          >
            <Eye className="h-4 w-4" />
            Vorschau
          </Button>
          {/* Nur beim Entwurf, und nur wenn er schon angelegt ist: Was geplant oder
              veroeffentlicht ist, wird erst zurueckgezogen (A5). */}
          {stand === "entwurf" && !istNeu && (
            <Button
              size="kompakt"
              variant="outline"
              disabled={arbeitet}
              onClick={() => {
                setLoeschfehler(false);
                setLoeschenOffen(true);
              }}
              className="gap-1.5 text-red-700 shadow-none hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
              Löschen
            </Button>
          )}
          {(veroeffentlicht || geplant) && (
            <Button size="kompakt" variant="outline" disabled={arbeitet} onClick={zurueckziehenJetzt}>
              Zurückziehen
            </Button>
          )}
          {/* Beim Entwurf ist "Veröffentlichen" die Hauptaktion daneben — dann bleibt
              Speichern zurueckhaltend, sonst streiten zwei Knoepfe um dieselbe Rolle. */}
          <Button
            size="kompakt"
            variant={veroeffentlicht ? "default" : "outline"}
            disabled={arbeitet}
            onClick={() => speichern()}
            className={veroeffentlicht ? "text-white" : undefined}
          >
            {veroeffentlicht ? "Änderung speichern" : "Entwurf speichern"}
          </Button>
          {!veroeffentlicht && (
            // Der Hinweis haengt am Knopf statt daneben. Die Huelle traegt ihn, weil ein
            // gesperrter Knopf keine Mausereignisse bekommt und seinen eigenen Tooltip
            // damit nie zeigt.
            <span title={fehlendes ?? undefined}>
              <Button
                size="kompakt"
                disabled={arbeitet || fehlendes !== null}
                onClick={zeitpunktFensterOeffnen}
                className="text-white"
              >
                {geplant ? "Zeitpunkt ändern" : "Veröffentlichen"}
              </Button>
            </span>
          )}
        </div>
      </div>

      {meldung && (
        <p className={`mb-4 text-sm ${meldung.art === "fehler" ? "text-red-600" : "text-green-700"}`}>{meldung.text}</p>
      )}

      <div className="flex flex-col items-center gap-4">
        {/* Was der Eintrag bewirkt hat (KAN-296). Nur bei Veroeffentlichtem: Ein Entwurf hat
            niemanden erreicht, da waere eine Null eine Behauptung. */}
        {gespeichert?.status === "veroeffentlicht" && gespeichert.zahlen && (
          // Eigener Abschnitt mit Namen: "Buttons" steht jetzt zweimal auf der Seite, hier
          // und unten in der Maske. Wer sich die Seite vorlesen laesst, braucht den
          // Unterschied genauso wie ein Test.
          <section aria-label="Statistik" className="w-full max-w-[560px] rounded-xl border border-gray-200 bg-white px-3.5 py-3">
            <div className="mb-2.5 flex items-baseline justify-between">
              <h2 className="text-[13px] font-semibold text-gray-900">Statistik</h2>
              <span className="text-[11px] text-gray-500">
                geschrieben von {gespeichert.author ?? "kandidat.io"}
              </span>
            </div>
            {/* Kennzahlen, aber jede mit ihrem Bezug im Wert: "1 Erreicht" sagte nicht, von
                wie vielen. Zwoelf ist viel bei dreissig und wenig bei dreihundert. Solange
                niemand den Eintrag gesehen hat, steht dort ein Satz - drei Nullen
                nebeneinander sagen nichts. */}
            {gespeichert.zahlen.erreicht === 0 ? (
              <p className="text-[13px] text-gray-500">Noch hat niemand die Neuigkeit gesehen.</p>
            ) : (
              <div className="grid grid-cols-3 gap-3">
                {kennzahlen(gespeichert.zahlen).map(kennzahl => (
                  <div key={kennzahl.titel}>
                    <div className="text-[11px] font-medium text-gray-500">{kennzahl.titel}</div>
                    <div className="text-lg font-semibold leading-tight text-gray-900">{kennzahl.wert}</div>
                    <div className="text-[11px] text-gray-500">{kennzahl.erklaerung}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Getrennt nach Knopf und Video, mit Ueberschrift: Vorher stand beides in einer
                Liste, und ob eine Zeile ein Knopf oder ein Video war, verriet nur das Wort
                ganz rechts. Die Beschriftungen sind die heutigen - wird ein Knopf umbenannt,
                zeigt die Auswertung den neuen Namen. */}
            {gespeichert.zahlen.erreicht > 0 &&
              (
                [
                  { ueberschrift: "Buttons", eintraege: gespeichert.zahlen.knoepfe, tat: "geklickt" },
                  { ueberschrift: "Videos", eintraege: gespeichert.zahlen.videos, tat: "angesehen" },
                ] as const
              )
                .filter(block => block.eintraege.length > 0)
                .map(block => (
                  <div key={block.ueberschrift} className="mt-3 border-t border-gray-100 pt-2.5">
                    <div className="mb-1 text-[11px] font-medium uppercase tracking-wide text-gray-500">{block.ueberschrift}</div>
                    <div className="space-y-1">
                      {block.eintraege.map(eintrag => (
                        <div key={eintrag.stelle} className="flex items-baseline justify-between gap-3 text-[13px]">
                          <span className="min-w-0 truncate text-gray-700">{eintrag.label || "Ohne Beschriftung"}</span>
                          <span className="shrink-0 text-gray-500">
                            <span className="font-medium text-gray-900">{eintrag.personen}</span> von{" "}
                            {gespeichert.zahlen?.geoeffnet ?? 0} {block.tat}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}

            {/* Das Ergebnis der Umfrage (KAN-302). Es steht nur hier - der Kunde bekommt
                es nicht zu sehen, und geschickt wird es ihm auch nicht. */}
            {gespeichert.zahlen?.umfrage && (
              <div className="mt-3 border-t border-gray-100 pt-2.5">
                <div className="mb-1.5 flex items-baseline justify-between gap-3">
                  <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Umfrage</div>
                  <span className="shrink-0 text-[11px] text-gray-500">
                    <span className="font-medium text-gray-900">{gespeichert.zahlen.umfrage.beteiligt}</span> von{" "}
                    {gespeichert.zahlen.geoeffnet} haben geantwortet
                  </span>
                </div>
                <p className="mb-2 text-[13px] text-gray-700">{gespeichert.zahlen.umfrage.frage}</p>

                <div className="space-y-2">
                  {gespeichert.zahlen.umfrage.antworten.map(antwort => (
                    <div key={antwort.stelle} className="space-y-1">
                      <div className="flex items-baseline justify-between gap-3 text-[13px]">
                        <span className="min-w-0 truncate text-gray-700">{antwort.label || "Ohne Beschriftung"}</span>
                        <span className="shrink-0 text-gray-500">
                          <span className="font-medium text-gray-900">{antwort.anteil} %</span> · {antwort.personen}
                        </span>
                      </div>
                      <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                        <div className="h-full rounded-full bg-primary" style={{ width: `${antwort.anteil}%` }} />
                      </div>
                      {/* Nur bei einer Umfrage, die das sagt: Bei einer anonymen kommt hier
                          nichts an, weil der Server die Namen gar nicht erst laedt. */}
                      {antwort.betriebe.length > 0 && <div className="text-[11px] leading-relaxed text-gray-500">{antwort.betriebe.join(" · ")}</div>}
                    </div>
                  ))}
                </div>

                {gespeichert.zahlen.umfrage.mehrfach && (
                  <p className="mt-2 text-[11px] leading-relaxed text-gray-400">
                    Mehrfachauswahl war an: Die Anteile zählen Personen je Antwort und ergeben zusammen mehr als 100 %.
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* Steht ueber der Karte, nicht darunter: Die Einordnung bestimmt, welche Fragen
            in den leeren Feldern stehen. Wer sie hinterher umstellt, hat den Text schon
            auf die falsche Frage geschrieben. */}
        <div className="flex w-full max-w-[560px] flex-wrap items-center gap-3 rounded-xl border border-gray-200 bg-white px-3.5 py-3">
          <label className="text-[13px] text-gray-700">
            Einordnung{" "}
            <select
              value={entwurf.group}
              onChange={event => setzen("group", event.target.value as NeuigkeitEinordnung)}
              className="ml-1 min-h-[34px] rounded-md border border-gray-200 bg-white py-1 pl-2 pr-7 text-[13px] focus:border-neuigkeit focus:ring-0"
            >
              {EINORDNUNGEN.map(eintrag => (
                <option key={eintrag.wert} value={eintrag.wert}>
                  {eintrag.label}
                </option>
              ))}
            </select>
          </label>

          <label className="inline-flex items-center gap-1.5 text-[13px] text-gray-700">
            <input
              type="checkbox"
              checked={entwurf.opensDialog}
              onChange={event => setzen("opensDialog", event.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-neuigkeit focus:ring-0 focus:ring-offset-0"
            />
            Beim Anmelden im Dialog öffnen
          </label>
        </div>

        <div className="w-full max-w-[560px] rounded-2xl border border-gray-200 bg-white p-7 shadow-sm">
          {/* Jedes Bild landet im selben Format: 16:9, mittig beschnitten (A10). */}
          <div
            className="relative -mx-7 -mt-7 mb-4 overflow-hidden rounded-t-2xl"
            onDragOver={event => {
              event.preventDefault();
              setUeberZiehen(true);
            }}
            onDragLeave={() => setUeberZiehen(false)}
            onDrop={bildFallenGelassen}
          >
            {gespeichert?.imageUrl ? (
              <img src={gespeichert.imageUrl} alt="" className="aspect-video w-full object-cover object-center" />
            ) : (
              // Ohne Bild ist die Flaeche selbst der Knopf. Den Rahmen traegt schon die Karte;
              // hier genuegt die Trennlinie nach unten, sonst stuenden zwei Linien uebereinander.
              <button
                type="button"
                disabled={arbeitet}
                onClick={() => bildFeld.current?.click()}
                className="flex aspect-video w-full flex-col items-center justify-center gap-2 border-b border-gray-200 bg-gray-50 text-gray-500 hover:bg-gray-100"
              >
                <ImagePlus className="h-7 w-7" strokeWidth={1.5} />
                <span className="text-sm font-medium text-gray-700">Bild hierher ziehen</span>
                <span className="text-[13px]">oder klicken zum Auswählen</span>
              </button>
            )}
            {/* Deckt beide Faelle ab: ueber dem Platzhalter wie ueber einem schon
                hochgeladenen Bild, das dabei ersetzt wird. */}
            {ueberZiehen && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center border-2 border-neuigkeit bg-neuigkeit-hell/90 text-sm font-medium text-neuigkeit-dunkel">
                Bild hier loslassen
              </div>
            )}
            {gespeichert?.imageUrl && (
              <div className="absolute bottom-3 right-3 flex gap-2">
                <Button size="kompakt" variant="outline" disabled={arbeitet} onClick={() => bildFeld.current?.click()} className="bg-white/95 shadow-none">
                  <ImagePlus className="mr-1.5 h-4 w-4" />
                  Bild ersetzen
                </Button>
                <Button
                  size="kompakt"
                  variant="outline"
                  disabled={arbeitet}
                  aria-label="Bild entfernen"
                  onClick={() => id && bildEntfernen(id)}
                  className="bg-white/95 shadow-none"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            )}
            <input ref={bildFeld} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={bildGewaehlt} />
          </div>

          <div className="mb-2.5 text-xs text-gray-500">{alsDeutschesDatum(entwurf.date)}</div>

          <label htmlFor="neuigkeit-titel" className="sr-only">
            Titel
          </label>
          <input
            id="neuigkeit-titel"
            // Kein Vorschlagsfenster des Browsers ueber der Karte: Was hier hingehoert,
            // weiss nur der Super-Admin, und frueher getippte Titel sind kein Vorrat
            // (KAN-292). Chrome rahmt das Feld blau, solange die Liste offen ist.
            autoComplete="off"
            value={entwurf.title}
            onChange={event => setzen("title", event.target.value)}
            placeholder="Worum geht es?"
            className={`w-full border-none bg-transparent px-1 -mx-1 text-[21px] font-bold leading-snug text-gray-900 placeholder:font-normal placeholder:text-gray-400 ${FOKUS_FELD}`}
          />

          <label htmlFor="neuigkeit-problem" className="sr-only">
            Das Problem
          </label>
          <MitwachsendesFeld
            id="neuigkeit-problem"
            wert={entwurf.problemText}
            onChange={wert => setzen("problemText", wert)}
            mindestZeilen={3}
            platzhalter={PLATZHALTER[entwurf.group].problem}
            className={`mt-3 w-full resize-none overflow-hidden border-none bg-transparent px-1 -mx-1 text-sm leading-relaxed text-gray-600 placeholder:text-gray-400 ${FOKUS_FELD}`}
          />

          <label htmlFor="neuigkeit-loesung" className="sr-only">
            Was wir geändert haben
          </label>
          <MitwachsendesFeld
            id="neuigkeit-loesung"
            wert={entwurf.solutionText}
            onChange={wert => setzen("solutionText", wert)}
            mindestZeilen={6}
            platzhalter={PLATZHALTER[entwurf.group].loesung}
            className={`mt-2.5 w-full resize-none overflow-hidden border-none bg-transparent px-1 -mx-1 text-sm leading-relaxed text-gray-700 placeholder:text-gray-400 ${FOKUS_FELD}`}
          />

          {entwurf.videos.map((eintrag, index) => {
            const video = videoZuLink(eintrag.url);
            if (!video) return null;
            return (
              <div key={index} className="relative mt-4">
                <VideoKarte video={{ ...video, title: eintrag.title || video.title }} />
                <button
                  type="button"
                  aria-label={`Video ${index + 1} entfernen`}
                  title="Video entfernen"
                  onClick={() =>
                    setzen(
                      "videos",
                      entwurf.videos.filter((_, nummer) => nummer !== index)
                    )
                  }
                  className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm hover:text-gray-700"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            );
          })}

          {/* Die Knoepfe stehen dort, wo sie beim Kunden auch stehen - unter den Absaetzen
              und dem Video, ueber der Linie. Was unter der Linie liegt, sieht nur der
              Super-Admin (KAN-292). */}
          {entwurf.actions.length > 0 && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {/* So, wie der Kunde sie sieht: innen gefuellt, aussen umrandet. Das kleine x
                liegt darueber, damit die Vorschau nicht zur Bedienleiste wird (KAN-292). */}
              {entwurf.actions.map((aktion, index) => (
                <span key={index} className="relative inline-flex">
                  <span
                    className={
                      aktion.kind === "extern"
                        ? "inline-flex min-h-9 items-center rounded-md border border-gray-300 px-3 text-sm font-medium text-gray-700"
                        : // Die Hausfarbe, nicht die der Neuigkeiten: Beim Kunden steht hier ein
                          // gewoehnlicher Knopf, und die Vorschau soll zeigen, was er sieht.
                          "inline-flex min-h-9 items-center rounded-md bg-primary px-3 text-sm font-medium text-white"
                    }
                  >
                    {aktion.label || "Button ohne Beschriftung"}
                  </span>
                  <button
                    type="button"
                    aria-label={`Button „${aktion.label || "ohne Beschriftung"}" entfernen`}
                    title="Button entfernen"
                    onClick={() =>
                      setzen(
                        "actions",
                        entwurf.actions.filter((_, nummer) => nummer !== index)
                      )
                    }
                    className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-400 shadow-sm hover:text-gray-700"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Die Umfrage steht in der Karte, zwischen Inhalt und Knoepfen - dort steht sie
              auch beim Kunden (KAN-302). */}
          {entwurf.umfrage && (
            <UmfrageFelder umfrage={entwurf.umfrage} onChange={umfrage => setzen("umfrage", umfrage)} onEntfernen={() => setzen("umfrage", null)} />
          )}

          {/* Die Knoepfe bleiben stehen und legen jeweils einen neuen Eintrag an
              (KAN-291). Wie viele es werden, entscheidet der Super-Admin. */}
          <div className="mt-5 flex flex-wrap gap-2.5 border-t border-gray-200 pt-4">
            <Button
              size="kompakt"
              variant="outline"
              className="border-dashed text-gray-600 shadow-none"
              onClick={() => setzen("actions", [...entwurf.actions, { label: "", kind: "intern", target: ziele[0]?.key ?? "" }])}
            >
              + Button
            </Button>
            <Button
              size="kompakt"
              variant="outline"
              className="border-dashed text-gray-600 shadow-none"
              onClick={() => setzen("videos", [...entwurf.videos, { url: "", title: "" }])}
            >
              + Video
            </Button>
            {/* Nur eine Umfrage je Neuigkeit: Wer drei Fragen in eine Karte stellt, bekommt
                auf die dritte keine Antwort (KAN-302). */}
            {!entwurf.umfrage && (
              <Button
                size="kompakt"
                variant="outline"
                className="border-dashed text-gray-600 shadow-none"
                onClick={() => setzen("umfrage", leereUmfrage())}
              >
                + Umfrage
              </Button>
            )}
          </div>
        </div>

        {entwurf.actions.length > 0 && (
          <div className="flex w-full max-w-[560px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-bold text-gray-900">Buttons</h2>
            {entwurf.actions.map((aktion, index) => (
              // Ab dem zweiten Block eine Linie: ohne sie laufen "Beschriftung" und
              // "Seite" zweimal hintereinander als ein einziger Stapel zusammen.
              <div
                key={index}
                className={`flex flex-wrap gap-2.5 ${index > 0 ? "border-t border-gray-100 pt-3.5" : ""}`}
              >
                <div className="min-w-[160px] flex-1">
                  <label htmlFor={`aktion-${index}-label`} className="mb-1 block text-[13px] font-medium text-gray-700">
                    Beschriftung
                  </label>
                  <Input
                    id={`aktion-${index}-label`}
                  autoComplete="off"
                    value={aktion.label}
                    onChange={event => aktionSetzen(index, { label: event.target.value })}
                    placeholder="Mehr erfahren"
                    className={`h-10 rounded-md border-gray-200 bg-white px-2 text-sm shadow-none focus-visible:ring-0 focus-visible:border-neuigkeit ${KEIN_AUTOFILL_BLAU}`}
                  />
                </div>
                <div className="min-w-[160px] flex-1">
                  <label htmlFor={`aktion-${index}-art`} className="mb-1 block text-[13px] font-medium text-gray-700">
                    Führt zu
                  </label>
                  <select
                    id={`aktion-${index}-art`}
                    value={aktion.kind}
                    onChange={event =>
                      aktionSetzen(index, {
                        kind: event.target.value as "intern" | "extern",
                        target: event.target.value === "intern" ? (ziele[0]?.key ?? "") : "",
                      })
                    }
                    className="h-10 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-neuigkeit focus:ring-0"
                  >
                    <option value="intern">Seite in kandidat.io</option>
                    <option value="extern">Externer Link</option>
                  </select>
                </div>
                <div className="w-full">
                  <label htmlFor={`aktion-${index}-ziel`} className="mb-1 block text-[13px] font-medium text-gray-700">
                    {aktion.kind === "intern" ? "Seite" : "Adresse"}
                  </label>
                  {aktion.kind === "intern" ? (
                    <select
                      id={`aktion-${index}-ziel`}
                  autoComplete="off"
                      value={aktion.target}
                      onChange={event => aktionSetzen(index, { target: event.target.value })}
                      className="h-10 w-full rounded-md border border-gray-200 bg-white px-2 text-sm focus:border-neuigkeit focus:ring-0"
                    >
                      {ziele.map(ziel => (
                        <option key={ziel.key} value={ziel.key}>
                          {ziel.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <Input
                      id={`aktion-${index}-ziel`}
                  autoComplete="off"
                      type="url"
                      value={aktion.target}
                      onChange={event => aktionSetzen(index, { target: event.target.value })}
                      placeholder="https://..."
                      className={`h-10 rounded-md border-gray-200 bg-white px-2 text-sm shadow-none focus-visible:ring-0 focus-visible:border-neuigkeit ${KEIN_AUTOFILL_BLAU}`}
                    />
                  )}
                </div>
                {/* Wie bei den Videos: Das kleine x in der Karte ist der schnelle Weg, hier
                    steht der benannte (KAN-292). */}
                <div className="w-full">
                  <Button
                    size="kompakt"
                    variant="outline"
                    className="gap-1.5 text-red-700 shadow-none hover:bg-red-50"
                    onClick={() =>
                      setzen(
                        "actions",
                        entwurf.actions.filter((_, nummer) => nummer !== index)
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" />
                    Button entfernen
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Eine Kachel fuer alle Videos, mit Ueberschrift - genau wie bei den Buttons
            (KAN-292). Ein Knopf in der Karte legt an, hier stehen die Felder (A12). */}
        {entwurf.videos.length > 0 && (
          <div className="flex w-full max-w-[560px] flex-col gap-3 rounded-xl border border-gray-200 bg-white p-4">
            <h2 className="text-sm font-bold text-gray-900">{entwurf.videos.length > 1 ? "Videos" : "Video"}</h2>
            {entwurf.videos.map((video, index) => {
              // Nicht erst nach dem Speichern: Sobald der Link aufgeloest ist, stehen
              // Vorschaubild, Herkunft und Beschriftung hier (KAN-292).
              const gespeichertesVideo = videoZuLink(video.url);
              return (
                // Ab dem zweiten eine Linie, sonst laufen zwei Videos als ein Stapel zusammen.
                <div key={index} className={`flex flex-col gap-3 ${index > 0 ? "border-t border-gray-100 pt-3.5" : ""}`}>

              <div>
                <label htmlFor={`video-link-${index}`} className="mb-1 block text-[13px] font-medium text-gray-700">
                  Link
                </label>
                <Input
                  id={`video-link-${index}`}
                  autoComplete="off"
                  type="url"
                  value={video.url}
                  onChange={event =>
                    setzen(
                      "videos",
                      entwurf.videos.map((eintrag, nummer) => (nummer === index ? { ...eintrag, url: event.target.value } : eintrag))
                    )
                  }
                  placeholder="https://www.loom.com/share/..."
                  className={`h-10 rounded-md border-gray-200 bg-white px-2 text-sm shadow-none focus-visible:border-neuigkeit focus-visible:ring-0 ${KEIN_AUTOFILL_BLAU}`}
                />
                {/* Zwei Stufen: Was die Maske selbst sieht, und was der Server zurueckmeldet.
                    Beides steht hier unten, nie in einem Fenster ueber der Seite (KAN-292). */}
                {linkBeanstandung(video.url) ? (
                  <p className="mt-1.5 text-xs text-red-700">{linkBeanstandung(video.url)}</p>
                ) : vorschauen[video.url.trim()] === null ? (
                  <p className="mt-1.5 text-xs text-red-700">Aus diesem Link lässt sich kein Video lesen.</p>
                ) : (
                  <p className="mt-1.5 text-xs text-gray-500">YouTube, Vimeo oder Loom</p>
                )}
              </div>

              {gespeichertesVideo && (
                <div className="flex items-start gap-3 border-t border-gray-100 pt-3">
                  <span className="relative flex h-[54px] w-24 shrink-0 items-center justify-center overflow-hidden rounded-md bg-gray-800">
                    {gespeichertesVideo.thumbnailUrl && (
                      <img src={gespeichertesVideo.thumbnailUrl} alt="" className="h-full w-full object-cover object-center" />
                    )}
                  </span>
                  <div className="min-w-0 flex-1">
                    <label htmlFor={`video-titel-${index}`} className="mb-1 block text-[13px] font-medium text-gray-700">
                      Beschriftung
                    </label>
                    <Input
                      id={`video-titel-${index}`}
                  autoComplete="off"
                      value={video.title}
                      onChange={event =>
                        setzen(
                          "videos",
                          entwurf.videos.map((eintrag, nummer) => (nummer === index ? { ...eintrag, title: event.target.value } : eintrag))
                        )
                      }
                      className={`h-10 rounded-md border-gray-200 bg-white px-2 text-sm shadow-none focus-visible:border-neuigkeit focus-visible:ring-0 ${KEIN_AUTOFILL_BLAU}`}
                    />
                    <p className="mt-1.5 text-xs text-gray-500">
                      {[ANBIETER_NAME[gespeichertesVideo.provider], gespeichertesVideo.duration].filter(Boolean).join(" \u00b7 ")}, beim Einfügen
                      geholt und überschreibbar
                      {/* Nur wenn es etwas zurueckzunehmen gibt: Ein Knopf ohne Wirkung
                          waere eine Einladung ins Leere (KAN-293). */}
                      {anbieterTitel(video.url) && video.title.trim() !== anbieterTitel(video.url) && (
                        <>
                          {". "}
                          <button
                            type="button"
                            onClick={() =>
                              setzen(
                                "videos",
                                entwurf.videos.map((eintrag, nummer) =>
                                  nummer === index ? { ...eintrag, title: anbieterTitel(video.url) ?? "" } : eintrag
                                )
                              )
                            }
                            className="underline underline-offset-2 hover:text-gray-700"
                          >
                            Titel des Anbieters nehmen
                          </button>
                        </>
                      )}
                    </p>
                  </div>
                </div>
              )}

              <div>
                <Button
                  size="kompakt"
                  variant="outline"
                  className="gap-1.5 text-red-700 shadow-none hover:bg-red-50"
                  onClick={() =>
                    setzen(
                      "videos",
                      entwurf.videos.filter((_, nummer) => nummer !== index)
                    )
                  }
                >
                  <Trash2 className="h-4 w-4" />
                  Video entfernen
                </Button>
              </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={loeschenOffen} onOpenChange={setLoeschenOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Diesen Entwurf löschen?</DialogTitle>
            <DialogDescription>
              „{entwurf.title || "Ohne Titel"}" wird endgültig entfernt. Rückgängig machen lässt sich das nicht.
            </DialogDescription>
          </DialogHeader>
          {loeschfehler && <p className="text-sm text-red-600">Das Löschen hat nicht geklappt. Der Entwurf ist unverändert erhalten.</p>}
          <div className="flex flex-wrap justify-end gap-2.5">
            <Button size="kompakt" variant="outline" onClick={() => setLoeschenOffen(false)}>
              Abbrechen
            </Button>
            <Button size="kompakt" disabled={arbeitet} onClick={loeschenJetzt} className="bg-red-700 text-white hover:bg-red-800">
              Entwurf löschen
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={zeitpunktOffen} onOpenChange={setZeitpunktOffen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Wann soll die Neuigkeit erscheinen?</DialogTitle>
          </DialogHeader>

          <div className="flex flex-col gap-2.5">
            <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm">
              <input
                type="radio"
                name="erscheinen"
                checked={sofort}
                onChange={() => setSofort(true)}
                className="mt-0.5 h-4 w-4 border-gray-300 text-neuigkeit focus:ring-0 focus:ring-offset-0"
              />
              <span>
                <span className="font-medium text-gray-900">Sofort veröffentlichen</span>
                <span className="block text-[13px] text-gray-600">Steht mit dem nächsten Laden bei allen Betrieben.</span>
              </span>
            </label>

            <label className="flex items-start gap-2.5 rounded-lg border border-gray-200 p-3 text-sm">
              <input
                type="radio"
                name="erscheinen"
                checked={!sofort}
                onChange={() => setSofort(false)}
                className="mt-0.5 h-4 w-4 border-gray-300 text-neuigkeit focus:ring-0 focus:ring-offset-0"
              />
              <span className="min-w-0">
                <span className="font-medium text-gray-900">Zu einem Zeitpunkt</span>
                <span className="block text-[13px] text-gray-600">Wird von selbst freigeschaltet, auf die Minute genau.</span>
                <input
                  type="datetime-local"
                  value={zeitpunkt}
                  disabled={sofort}
                  onChange={event => setZeitpunkt(event.target.value)}
                  aria-label="Zeitpunkt"
                  className="mt-2 min-h-[34px] rounded-md border border-gray-200 bg-white px-2 py-1 text-[13px] disabled:bg-gray-50 disabled:text-gray-400"
                />
              </span>
            </label>
          </div>

          <div className="flex flex-wrap justify-end gap-2.5">
            <Button size="kompakt" variant="outline" onClick={() => setZeitpunktOffen(false)}>
              Abbrechen
            </Button>
            <Button
              size="kompakt"
              disabled={arbeitet || (!sofort && !zeitpunkt)}
              // Der Browser liefert oertliche Zeit ohne Zone; `new Date` liest sie genauso
              // und `toISOString` macht daraus den Zeitpunkt, den der Server erwartet.
              onClick={() => veroeffentlichenMitSpeichern(sofort ? undefined : new Date(zeitpunkt).toISOString())}
              className="text-white"
            >
              {sofort ? "Jetzt veröffentlichen" : "Einplanen"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {vorschauOffen && (
        <KundenVorschau
          inhalt={alsKundeninhalt()}
          datum={alsDeutschesDatum(entwurf.date)}
          onClose={() => setVorschauOffen(false)}
        />
      )}
    </>
  );
};

export default NeuigkeitEditor;
